import PropTypes from "prop-types";
import { useState } from "react";
import { Collapse, Input, Label, ListGroupItem } from "reactstrap";

import {
  DefaultButton,
  H2,
  MUIIcons,
  SecondaryButton,
} from "../../AbstractElements";
import Divider from "../../CommonElements/Divider";
import FixedFooter from "../../CommonElements/FixedFooter";
import SliderModal from "../../CommonElements/SliderModal";
import { Apply, ClearAll, Filter, Search } from "../../utils/Constant";

import "./style.scss";

const DynamicFilterPanel = ({ config, values, onChange, onClear, onApply }) => {
  const [searchText, setSearchText] = useState<Record<string, string>>({});
  const [collapseMap, setCollapseMap] = useState<Record<string, boolean>>({});
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (key, value, type) => {
    const currentValue = values[key];
    if (type === "radio") {
      onChange({ ...values, [key]: value });
    } else if (type === "checkbox" || type === "searchable-checkbox") {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      const updatedArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      onChange({ ...values, [key]: updatedArray });
    }
  };

  const filteredOptions = filter => {
    if (filter.searchable) {
      const search = searchText[filter?.key]?.toLowerCase() || "";
      return filter?.options?.filter(opt =>
        opt.label.toLowerCase().includes(search)
      );
    }
    return filter.options;
  };

  const toggle = () => setIsOpen(!isOpen);

  const handleClear = () => {
    onClear();
    toggle();
  };

  const handleApply = () => {
    onApply();
    toggle();
  };

  return (
    <div className="dynamic-filter">
      <MUIIcons
        className="pointer primary-icon-color"
        onClick={toggle}
        iconName="FilterAltOutlined"
        size={24}
      />

      <SliderModal
        isOpen={isOpen}
        toggle={() => {
          toggle();
        }}
      >
        <H2>{Filter}</H2>
        <Divider />
        <div className="pt-2">
          {config.map(filter => {
            const options = filteredOptions(filter);
            const value = values[filter?.key];

            const content = (
              <>
                {filter.searchable && (
                  <Input
                    type="text"
                    placeholder={Search}
                    value={searchText[filter.key] || ""}
                    onChange={e =>
                      setSearchText({
                        ...searchText,
                        [filter.key]: e.target.value,
                      })
                    }
                    className="my-2"
                  />
                )}
                {options.map(option => (
                  <ListGroupItem key={option.value} className="p-0 border-0">
                    <Label
                      check
                      className="d-flex align-items-center gap-2 p-2"
                    >
                      <Input
                        type={filter.type === "radio" ? "radio" : "checkbox"}
                        name={filter.key}
                        checked={
                          filter.type === "radio"
                            ? value === option.value
                            : Array.isArray(value) &&
                              value.includes(option.value)
                        }
                        onChange={() =>
                          handleChange(filter.key, option.value, filter.type)
                        }
                        className="mt-0"
                      />
                      {option.label}
                    </Label>
                  </ListGroupItem>
                ))}
              </>
            );

            return (
              <div key={filter.key} className="mb-3">
                <div
                  onClick={() =>
                    filter.collapsible &&
                    setCollapseMap(prev => ({
                      ...prev,
                      [filter.key]: !prev[filter.key],
                    }))
                  }
                  className="dynamic-filter__element p-2 d-flex align-items-center justify-content-between pointer"
                >
                  <div>{filter.label}</div>

                  <MUIIcons
                    iconName={
                      collapseMap[filter.key]
                        ? "KeyboardArrowUpOutlined"
                        : "KeyboardArrowDownOutlined"
                    }
                    size={20}
                    className="trainer-dropdown-icon"
                  />
                </div>
                {filter.collapsible ? (
                  <Collapse isOpen={!!collapseMap[filter.key]}>
                    <div className="dynamic-filter__wrapper">{content}</div>
                  </Collapse>
                ) : (
                  content
                )}
              </div>
            );
          })}

          <div>
            <FixedFooter>
              <SecondaryButton onClick={handleClear}>
                {ClearAll}
              </SecondaryButton>
              <DefaultButton color="primary" onClick={handleApply}>
                {Apply}
              </DefaultButton>
            </FixedFooter>
          </div>
        </div>
      </SliderModal>
    </div>
  );
};

DynamicFilterPanel.propTypes = {
  config: PropTypes.array,
  values: PropTypes.object,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onApply: PropTypes.func,
};

export default DynamicFilterPanel;
